import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import LightRays from "@/components/LightRays";

export function CTA() {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);

  return (
    <section id="cta-section" className="relative overflow-hidden bg-foreground">
      {/* LightRays background */}
      <div className="absolute inset-0 pointer-events-none">
        <LightRays
          raysOrigin="bottom-center"
          raysColor="#ffffff"
          raysSpeed={0.5}
          lightSpread={0.2}
          rayLength={1.6}
          pulsating={false}
          fadeDistance={3}
          saturation={1}
          followMouse
          mouseInfluence={0.1}
          noiseAmount={0}
          distortion={0}
        />
      </div>

      <div className="container px-6 relative z-10 py-28 lg:py-36">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h2
            className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-8 leading-[1.05] text-primary-foreground text-balance"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t("Ready to Grow Your Business")} <span className="text-gradient">{t("Online in Kuwait?")}</span>
          </motion.h2>
          <motion.p
            className="text-primary-foreground/50 text-lg mb-12 max-w-xl mx-auto leading-relaxed text-balance"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            {t(
              "Tell us about your project and Kuwait's top web, eCommerce and AI team will come back to you within one business day.",
            )}
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 [&>*]:w-full [&>*]:sm:w-auto [&>*]:justify-center"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.16 }}
          >
            <Button variant="hero" size="xl" asChild>
              <Link to={localePath("/start-a-project")}>
                {t("Get a Free Quote")}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button
              size="xl"
              asChild
              className="border-2 border-primary-foreground/20 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground/40"
            >
              <a href="https://wa.me/+96597558522" target="_blank" rel="noopener noreferrer">
                <svg className="w-5 h-5 mr-1" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                {t("Drop a message")}
              </a>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
